<template>
  <div class="h-full flex flex-col">
    <div
      class="flex-shrink h-10 w-full dark:bg-gray-900 dark:text-white"
      :class="[
        'flex items-center flex-row',
        navClass
      ]"
    >
      <slot name="leading" />
      <template v-if="showArrows">
        <o-button flat size="sm" icon="chevronLeft" @click="handleMoveScroll(-1)" />
        <div class="py-2 pl-2 h-full">
          <span class="border-r h-full dark:border-gray-800" />
        </div>
      </template>
      <div class="flex-grow flex-shrink w-10 h-10">
        <o-scroll
          ref="scrollRef"
          x-axis
          :y-axis="false"
          :options="{
            visibility: 'hidden'
          }"
          draggable
        >
          <div
            class="flex relative h-full items-center px-2"
          >
            <template v-for="(tab, i) in tabs" :key="i">
              <button
                v-if="tab.isVisible"

                :ref="tab.hash"
                class="z-10 flex h-8 flex-shrink-0 items-center text-sm -mb-px leading-none hover:text-primary-600 focus:outline-none relative"
                :class="[
                  pill ? 'px-4 mr-px' : 'px-2 mr-2 md:mr-4',
                  tab.isActive ? (pill ? 'opacity-80 font-medium' : 'text-primary-600 font-medium dark:text-primary-400') : 'text-gray-600 dark:text-gray-300',
                  tab.tabClass
                ]"
                @click="selectTab(tab.hash, $event)"
              >
                <UIcon v-if="tab.icon && tab.icon.startsWith('lucide:')" :name="tab.icon" class="lg:mr-3 p-2 md:p-0 hidden lg:block" />
                <o-icon v-else-if="tab.icon" :icon="tab.icon" :size="16" class="lg:mr-3 p-2 md:p-0 hidden lg:block" />
                <span class="">{{ tab.name }}</span>

                <span v-if="tab.alert" class="block mb-px ml-2 bg-gray-200 dark:bg-white/10 text-gray-700 dark:text-gray-200 p-1 leading-none rounded text-xs font-medium">
                  {{ tab.alert }}
                </span>
              </button>
            </template>
            <span
              :class="[
                'absolute transition-all ease-in-out duration-200',
                pill ? 'h-8 bg-gray-100 rounded-md top-1/2 transform -translate-y-1/2 dark:bg-gray-900' : 'h-2px bottom-0 bg-primary-600 shadow'
              ]"
              :style="underline"
            />
          </div>
        </o-scroll>
      </div>
      <template v-if="showArrows">
        <div class="py-2 pr-2 h-full">
          <span class="border-r h-full dark:border-gray-800" />
        </div>
        <o-button flat size="sm" icon="chevronRight" @click="handleMoveScroll(1)" />
      </template>
      <slot name="trailing" />
    </div>

    <o-scroll
      :y-axis="scrollable"
      :class="[
        containerClass
      ]"
    >
      <slot ref="content" />
    </o-scroll>
  </div>
</template>

<script setup lang="ts">
import type { TabRegistration } from './Tab.vue'

const instance = getCurrentInstance()
const route = useRoute()

interface Props {
  navClass?: string
  containerClass?: string
  scroll?: boolean
  pill?: boolean
  showArrows?: boolean
  showHeader?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  navClass: 'border-b border-gray-200 px-5 dark:border-gray-800',
  containerClass: 'pt-4 pb-4',
  scroll: false,
  pill: false,
  showArrows: true,
  showHeader: false,
})

const emit = defineEmits<{
  (e: 'clicked', payload: { tab: any }): void
  (e: 'changed', payload: { tab: any }): void
}>()

const scrollRef = ref<any>()
const registeredTabs = ref<TabRegistration[]>([])
const activeTabHash = ref('')
const activeTabIndex = ref(0)
const lastActiveTabHash = ref('')
const firstTabIndex = ref(0)
const scrollable = ref(false)

// Provide register/unregister functions for child Tab components
provide('tabs-register', (tab: TabRegistration) => {
  registeredTabs.value.push(tab)
})

provide('tabs-unregister', (tab: TabRegistration) => {
  const index = registeredTabs.value.indexOf(tab)
  if (index > -1) {
    registeredTabs.value.splice(index, 1)
  }
})

const tabs = computed(() => {
  return registeredTabs.value
})

const underline = computed(() => {
  const activeRefs = instance?.proxy?.$refs[activeTabHash.value] as any

  let left = '0px'
  let width = '0px'

  if (activeRefs) {
    left = activeRefs[0].offsetLeft + 'px'
    width = activeRefs[0].getBoundingClientRect().width + 'px'
  }

  return { left, width }
})

const activeTab = computed(() => {
  return findTab(activeTabHash.value)
})

function findTab (hash: string) {
  return tabs.value.find((tab: any) => tab.hash === hash)
}

function selectTab (selectedTabHash: string, event?: Event) {
  const selectedTab = findTab(selectedTabHash)
  if (!selectedTab) { return }
  if (event && selectedTab.isDisabled) { event.preventDefault(); return }
  if (lastActiveTabHash.value === selectedTab.hash) {
    emit('clicked', { tab: selectedTab })
    return
  }
  tabs.value.forEach((tab: any) => {
    tab.isActive = (tab.hash === selectedTab.hash)
  })
  emit('changed', { tab: selectedTab })
  activeTabHash.value = selectedTab.hash
  activeTabIndex.value = getTabIndex(selectedTabHash)
  lastActiveTabHash.value = activeTabHash.value = selectedTab.hash

  nextTick(() => {
    scrollable.value = props.scroll || selectedTab?.scroll || false
  })
}

function selectTabByIndex (index: number) {
  const hash = getTabHash(index)
  if (hash) { selectTab(hash) }
}

function setTabVisible (hash: string, visible: boolean) {
  const tab = findTab(hash)
  if (!tab) { return }
  tab.isVisible = visible
  if (tab.isActive) {
    tab.isActive = visible
    tabs.value.every((t: any) => {
      if (t.isVisible) { t.isActive = true; return false }
      return true
    })
  }
}

function getTabIndex (hash: string) {
  const tab = findTab(hash)
  return tabs.value.indexOf(tab)
}

function getTabHash (index: number) {
  const tab = tabs.value.find((t: any) => tabs.value.indexOf(t) === index)
  if (!tab) { return }
  return tab.hash
}

function getActiveTab () {
  return findTab(activeTabHash.value)
}

function getActiveTabIndex () {
  return getTabIndex(activeTabHash.value)
}

function handleMoveScroll (position = 1) {
  const element = scrollRef.value
  const current = activeTabIndex.value
  const tabsList = tabs.value

  const index = current + position

  if (index >= 0 && index < tabsList.length) {
    const hash = tabsList[index].hash
    const tab = instance?.proxy?.$refs[hash] as any

    if (tab && element) {
      element.scroll(tab[0], 500)
      selectTab(hash)
      activeTabIndex.value = index
    }
  }
}

onMounted(() => {
  nextTick(() => {
    if (tabs.value.length) {
      let initial = 0

      for (const index in tabs.value) {
        const tab = tabs.value[index]
        tab.showHeader = props.showHeader

        if (tab.initial) {
          initial = Number(index)
        }
      }

      selectTab(tabs.value[initial].hash)

      if (route?.query?.tab) {
        const query = route?.query?.tab
        const hash = '#' + query

        if (hash) {
          selectTab(hash)
        }
      }
    }
  })
})

defineExpose({ selectTab, selectTabByIndex, setTabVisible, getActiveTab, getActiveTabIndex })
</script>
